import { HttpResponse } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { defer, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { ApiService } from './api.service';
import { SESSION_HEARTBEAT_PATH, SESSION_HEARTBEAT_TIMEOUT_MS } from './session-activity.service';
import { UserService } from './user.service';

export { SESSION_HEARTBEAT_PATH, SESSION_HEARTBEAT_TIMEOUT_MS };


/** Parsed heartbeat outcome. `off` = the server reported session enforcement is off (no timeout fields). */
export type SessionHeartbeatResult =
  | {
      readonly mode: 'off';
      readonly sentAtMs: number;
      /** Client time the response arrived (D1/C4); optional for callers that predate it (then "now" is used). */
      readonly receivedAtMs?: number;
      /** Tenant of the bearer the request was sent with (read when it left), or `null`. */
      readonly tenantId?: number | null;
    }
  | {
      readonly mode: 'enforced';
      readonly sentAtMs: number;
      /** Client time the response arrived, captured when it arrived (C2/C4: logout = receipt time + remaining). */
      readonly receivedAtMs?: number;
      /** The raw `result` object, so the session-policy parser sees the wire field names. */
      readonly result: Record<string, unknown>;
      /** Server-measured idle time left right after the touch, in ms; `null` when absent/invalid. */
      readonly idleRemainingMs: number | null;
      /**
       * D7: customer id (`rt.cid`) of the token the request was sent with, read when it left; `null` when unknown. The
       * response itself names no tenant, so a result is attributed to the tenant of the bearer it was sent under.
       */
      readonly tenantId?: number | null;
    };

/** Monotonic milliseconds for round-trip measurement (wall-clock fallback where `performance` is unavailable). */
function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
}

/** A heartbeat that did not produce a usable success (non-200, `success:false`, malformed body). */
export class SessionHeartbeatError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'SessionHeartbeatError';
  }
}

/**
 * Makes the single authenticated heartbeat call: `POST {API_BASE_URL}/token/session-heartbeat`
 * with an empty JSON body, through the app's normal `ApiService` path (bearer + HMAC headers,
 * request timeout, the session-status interceptor for 401). No retry: the caller decides.
 * `ApiService` is resolved lazily to keep DI construction order out of the warning service.
 */
@Injectable({ providedIn: 'root' })
export class SessionHeartbeatService {
  private readonly injector = inject(Injector);

  /**
   * Sends one heartbeat.
   * @returns Emits the parsed result once; errors on a network failure, 503, any non-200, or a malformed body.
   */
  beat(): Observable<SessionHeartbeatResult> {
    return defer(() => {
      const sentMono = monotonicNow();
      const url = `${environment.API_BASE_URL}${SESSION_HEARTBEAT_PATH}`;
      const tenantId = this.currentTenant();
      // D8: bounded wait (ApiService's own timeout option; a timeout cancels the request and errors with HTTP 408).
      return this.injector.get(ApiService).postEmpty<unknown>(url, { timeoutMs: SESSION_HEARTBEAT_TIMEOUT_MS }).pipe(
        map((res) => {
          // R is captured when the response ARRIVES; S = R - monotonic round trip (contract C1-C5).
          const receivedAtMs = Date.now();
          const sentAtMs = receivedAtMs - Math.max(0, monotonicNow() - sentMono);
          return this.parse(res, sentAtMs, tenantId, receivedAtMs);
        })
      );
    });
  }

  /**
   * Validates the response envelope.
   * @param res - Full HTTP response.
   * @param sentAtMs - Time the request was sent.
   * @returns The parsed result.
   * @throws SessionHeartbeatError when the response is not a 200 `{ success: true, result: {...} }`.
   */
  private parse(res: HttpResponse<unknown>, sentAtMs: number, tenantId: number | null, receivedAtMs: number): SessionHeartbeatResult {
    if (res.status !== 200) {
      throw new SessionHeartbeatError(`Heartbeat returned HTTP ${res.status}`, res.status);
    }
    const body = res.body as { success?: unknown; result?: unknown } | null;
    const result = body?.result;
    if (!body || body.success !== true || typeof result !== 'object' || result === null) {
      throw new SessionHeartbeatError('Heartbeat response was not a success envelope', res.status);
    }
    const raw = result as Record<string, unknown>;
    if (raw['session_enforcement'] === 'off') {
      return { mode: 'off', sentAtMs, receivedAtMs, tenantId };
    }
    const seconds = raw['idle_remaining_seconds'];
    const idleRemainingMs = typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0 ? Math.round(seconds * 1000) : null;
    return { mode: 'enforced', sentAtMs, receivedAtMs, result: raw, idleRemainingMs, tenantId };
  }

  /**
   * D7: the `rt.cid` CLAIM of the bearer the request is sent with, or `null` when none/unusable. Best effort: never throws.
   * Deliberately NOT `getCustomerId()`: that falls back to a legacy stored id when the token has no usable claim, which could
   * attribute a result to a tenant the bearer does not name.
   */
  private currentTenant(): number | null {
    try {
      const id = this.injector.get(UserService).sessionTenantId();
      if (id === null) {
        return null;
      }
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }
}
